import {
  Button,
  Column,
  Columns,
  FlexBox,
  Icon,
  Modal,
  ModalBody,
  Spinner,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useEffect, useRef } from 'react';

import { appActions } from 'app/app.actions';
import { CommandPaletteScope, useCommandPalette } from 'utils/commandPalette';
import { createAppModal } from 'utils/modal';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import styles from './commandPalette.module.scss';
import { ConversationScopeList } from './components/conversationScopeList/conversationScopeList';
import { MacroScopeList } from './components/macroScopeList/macroScopeList';
import { MainSelectionList } from './components/mainScopeList/mainScopeList';
import { UserScopeList } from './components/userScopeList/userScopeList';
import { useRegisterDefaultActions } from './hooks/useRegisterDefaultActions';

/** Converts CommandPaletteScope to a string for tracking. */
const getScopeName = (scope: CommandPaletteScope): string => {
  switch (scope) {
    case CommandPaletteScope.User:
      return 'user';
    case CommandPaletteScope.Conversation:
      return 'conversation';
    case CommandPaletteScope.Macro:
      return 'macro';
    default:
      return 'main';
  }
};

/**
 * Represents the component for displaying a command palette that can be
 * triggered with a hotkey.
 */
export const CommandPalette = createAppModal(
  { type: 'app:commandPalette', featureFlag: 'cmd-k' },
  ({ isOpen, hide }): JSX.Element => {
    const dispatch = useAppDispatch();
    const { asyncStatus, query, scope, setIsActive, setQuery } =
      useCommandPalette();
    const inputRef = useRef<HTMLInputElement>(null);

    // Register default navigation actions
    useRegisterDefaultActions();

    // Handles closing the command palette (via ESC or click outside)
    const handleClose = useCallback(() => {
      ampli.commandPaletteClosed({
        closedBy: 'escape', // Modal handles both ESC and click outside
        query: query || undefined,
        scope: getScopeName(scope ?? CommandPaletteScope.Main),
      });
      setQuery('');
      hide();
    }, [query, scope, hide, setQuery]);

    // Handles resetting the search when the command palette closes.
    useEffect(() => {
      if (!isOpen) {
        setQuery('');
      }
    }, [isOpen, setQuery]);

    // Focus input when query is programmatically set (e.g., @, #, or !)
    useEffect(() => {
      if (isOpen && (query === '@' || query === '#' || query === '!')) {
        inputRef.current?.focus();
      }
    }, [isOpen, query]);

    // Opens the feedback modal
    const handleFeedbackClick = useCallback(() => {
      dispatch(
        appActions.showModal({
          modal: {
            type: 'app:feedbackForm',
            arg: {
              title: 'Command palette feedback',
              team: 'conversations',
              placeholder:
                'Tell us what you think about the command palette (Cmd+K).',
            },
          },
        }),
      );
      hide();
    }, [dispatch, hide]);

    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        allowClickOutside
        className={styles.modal}
      >
        <ModalBody className={styles.container}>
          <Columns className={styles.panel}>
            <Column>
              <Column.Row
                className={styles.search}
                noHorizontalPadding
                noVerticalPadding
                noMinHeight
                borderBottom
              >
                <FlexBox alignItems="center" gap="200">
                  {asyncStatus === 'pending' ? (
                    <Spinner size={16} />
                  ) : (
                    <Icon name="search" />
                  )}

                  <input
                    ref={inputRef}
                    className={styles.input}
                    autoFocus
                    placeholder="Search commands, @ contacts, # conversations, ! macros..."
                    value={query}
                    onFocus={() => setIsActive(true)}
                    onBlur={() => setIsActive(false)}
                    onChange={(event) => setQuery(event.target.value)}
                  />
                </FlexBox>
              </Column.Row>

              <Column.Row
                scrollable
                size={1}
                noHorizontalPadding
                noVerticalPadding
                noMinHeight
                className={styles.results}
              >
                {scope === CommandPaletteScope.Main && <MainSelectionList />}

                {scope === CommandPaletteScope.Conversation && (
                  <ConversationScopeList />
                )}

                {scope === CommandPaletteScope.User && <UserScopeList />}

                {scope === CommandPaletteScope.Macro && <MacroScopeList />}
              </Column.Row>

              <Column.Row
                noHorizontalPadding
                noVerticalPadding
                noMinHeight
                borderTop
                className={styles.hints}
              >
                <FlexBox alignItems="center" justifyContent="space-between">
                  <FlexBox alignItems="center" gap="400">
                    <span>
                      <kbd>↑↓</kbd> Navigate
                    </span>
                    <span>
                      <kbd>↵</kbd> Select
                    </span>
                    <span>
                      <kbd>esc</kbd> Close
                    </span>
                  </FlexBox>
                  <Button variant="tertiary" onClick={handleFeedbackClick}>
                    Give feedback
                  </Button>
                </FlexBox>
              </Column.Row>
            </Column>
          </Columns>
        </ModalBody>
      </Modal>
    );
  },
);
